import React, { useState, useEffect } from 'react';
import { TIMEZONES } from '../../utils/timezoneConstants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bell, Clock, Mail, Globe, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const parseTime = (hhmm) => {
  const [h, m] = (hhmm || '17:15').split(':');
  const hr24 = parseInt(h);
  return {
    hour: String(hr24 > 12 ? hr24 - 12 : hr24 || 12),
    minute: m || '15',
    ampm: hr24 >= 12 ? 'PM' : 'AM',
  };
};

const buildTime = (hour, minute, ampm) => {
  let h = parseInt(hour);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

const DSUAutomationTab = () => {
  const [config, setConfig] = useState({ enabled: false, run_time: '17:15', utc_offset: 'America/New_York', mode: 'draft', delivery: 'email', cc_admin: false });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeParts, setTimeParts] = useState({ hour: '5', minute: '15', ampm: 'PM' });
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get('/dsu-automation/config').then(r => {
      const cfg = r.data || {};
      setConfig({
        ...cfg,
        // Normalize legacy numeric UTC offsets to IANA string so Save always persists a valid timezone
        utc_offset: typeof cfg.utc_offset === 'string' ? cfg.utc_offset : 'America/New_York',
      });
      setTimeParts(parseTime(cfg.run_time || '17:15'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateTime = (field, val) => {
    const next = { ...timeParts, [field]: val };
    setTimeParts(next);
    setConfig(c => ({ ...c, run_time: buildTime(next.hour, next.minute, next.ampm) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/dsu-automation/config', config);
      toast.success('Daily report automation settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const handleRunNow = async () => {
    setRunning(true); setLastResult(null);
    try {
      const res = await api.post('/dsu-automation/run-now');
      setLastResult(res.data);
      toast.success(res.data.message);
    } catch { toast.error('Failed to run reports'); }
    setRunning(false);
  };

  if (loading) return <div className="text-sm text-muted-foreground py-4">Loading...</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Automated Daily Sale Updates</CardTitle>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))}
              data-testid="dsu-automation-toggle"
            />
          </div>
          <CardDescription>
            Automatically generate Daily Sale Update reports for every active horse in live sales.
          </CardDescription>
        </CardHeader>
        {config.enabled && (
          <CardContent className="space-y-6">
            {/* Run Time */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Clock className="w-4 h-4" /> Daily Run Time</Label>
              <div className="flex items-center gap-2">
                <Select value={timeParts.hour} onValueChange={v => updateTime('hour', v)}>
                  <SelectTrigger className="w-20 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-muted-foreground">:</span>
                <Select value={timeParts.minute} onValueChange={v => updateTime('minute', v)}>
                  <SelectTrigger className="w-20 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={timeParts.ampm} onValueChange={v => updateTime('ampm', v)}>
                  <SelectTrigger className="w-20 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeof config.utc_offset === 'string' ? config.utc_offset : 'America/New_York'} onValueChange={v => {
                    setConfig(c => ({ ...c, utc_offset: v }));
                }}>
                  <SelectTrigger className="w-72 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">Default: 5:15 PM. Reports run for any horse still active at run time.</p>
            </div>

            {/* Report Grouping */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Bell className="w-4 h-4" /> Report Grouping</Label>
              <div className="flex gap-3">
                {[
                  { value: 'per_horse', label: 'Per Horse', desc: 'One report per horse per owner' },
                  { value: 'all_horses', label: 'All Horses per Owner', desc: 'One combined report per owner with all their horses' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`flex-1 p-3 border rounded cursor-pointer transition-colors ${(config.grouping || 'per_horse') === opt.value ? 'border-primary bg-[rgba(52,107,78,0.06)]' : 'hover:bg-muted/30'}`}
                    onClick={() => setConfig(c => ({ ...c, grouping: opt.value }))}
                  >
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mode */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><CheckSquare className="w-4 h-4" /> Report Mode</Label>
              <div className="flex gap-3">
                {[
                  { value: 'draft', label: 'Save as Draft', desc: 'Reports saved for review before sending' },
                  { value: 'auto_send', label: 'Auto-Send to Owners', desc: 'Reports sent immediately without review' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`flex-1 p-3 border rounded cursor-pointer transition-colors ${config.mode === opt.value ? 'border-primary bg-[rgba(52,107,78,0.06)]' : 'hover:bg-muted/30'}`}
                    onClick={() => setConfig(c => ({ ...c, mode: opt.value }))}
                  >
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery — only shown when Auto-Send is selected */}
            {config.mode === 'auto_send' && (
            <div className="space-y-2">
              <Label>Owner Delivery Method</Label>
              <div className="flex gap-3">
                {[
                  { value: 'email', label: 'Email', icon: Mail },
                  { value: 'portal', label: 'Portal Only', icon: Globe },
                  { value: 'both', label: 'Email + Portal', icon: Bell },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`flex-1 p-3 border rounded cursor-pointer transition-colors flex items-center gap-2 ${config.delivery === opt.value ? 'border-primary bg-[rgba(52,107,78,0.06)]' : 'hover:bg-muted/30'}`}
                    onClick={() => setConfig(c => ({ ...c, delivery: opt.value }))}
                  >
                    <opt.icon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* CC Admin — only shown when Auto-Send + email delivery */}
            {config.mode === 'auto_send' && (config.delivery === 'email' || config.delivery === 'both') && (
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="cc-admin"
                  checked={!!config.cc_admin}
                  onCheckedChange={v => setConfig(c => ({ ...c, cc_admin: !!v }))}
                  data-testid="dsu-cc-admin-checkbox"
                />
                <label
                  htmlFor="cc-admin"
                  className="text-sm cursor-pointer select-none"
                >
                  Include me in CC
                </label>
              </div>
            )}
          </CardContent>
        )}
        <div className="px-6 pb-4 flex items-center justify-between border-t pt-4">
          <Button variant="outline" onClick={handleRunNow} disabled={running}>
            {running ? 'Running...' : 'Run Now'}
          </Button>
          {lastResult && (
            <span className="text-sm text-muted-foreground">{lastResult.message}</span>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default DSUAutomationTab;
